import { useCallback, useEffect, useMemo, useState } from 'react';

import { PopoverTrigger } from '@radix-ui/react-popover';
import { createColumnHelper, getCoreRowModel, PaginationState, useReactTable } from '@tanstack/react-table';
import { useParams, useSearchParams } from 'react-router-dom';

import { InconsistenciesLoader } from './components/inconsistencies-loader';
import { Permission } from '../../../../../../../../../backend/src/auth/permissions/permission';
import { Inconsistency } from '../../../../../../../../../backend/src/matter/inconsistency/inconsistency.entity';
import { DownloadFormat } from '../../../../../../../../../backend/src/types/types';
import { PermissionGate } from '../../../../../../../hooks/useHasPermission';
import { useDeleteInconsistency } from '@/api/mutations/useDeleteInconsistency';
import {
  inconsistencyDownloadColumnOptions,
  useDownloadInconsistencies,
} from '@/api/mutations/useDownloadInconsistencies';
import { InconsistencyFilters, useGetSubsetInconsistencies } from '@/api/queries/useGetAllSubsetInconsistencies';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { CountBadge } from '@/new/components/count-badge';
import { DownloadTableModal } from '@/new/components/dialogs/download-dialog/download-table';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { FilterPopover } from '@/new/components/inconsistency/filter-popover';
import { InconsistencyCard } from '@/new/components/inconsistency/inconsistency-card';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover } from '@/new/components/shadcn/popover';
import { SubsetChip } from '@/new/components/subset-chip';
import { TablePagination } from '@/new/components/table/table-pagination';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { MyOptionType } from '@/types';
import { buildInconsistencyFilterPills } from '@/utils/buildFilterPills';

const fallbackTableData: Inconsistency[] = [];
const columnHelper = createColumnHelper<Inconsistency>();

const columns = [
  columnHelper.accessor('level', {}),
  columnHelper.accessor('description', {}),
  columnHelper.accessor('type', {}),
  columnHelper.accessor('source', {}),
];

const defaultFilters: InconsistencyFilters = {};

export function Inconsistencies() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const [searchParams, setSearchParams] = useSearchParams();

  // Server-side pagination state - initialised from URL params
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: Math.max(0, parseInt(searchParams.get('page') || '0', 10)) || 0,
    pageSize: Math.max(1, parseInt(searchParams.get('pageSize') || '50', 10)) || 50,
  }));
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const [filters, setFilters] = useState<InconsistencyFilters>(() => {
    const types = searchParams.getAll('types');
    const levels = searchParams.getAll('levels');
    const sources = searchParams.getAll('sources');
    const hasRelevantFacts = searchParams.get('hasRelevantFacts') === 'true';
    const isPleadingParam = searchParams.get('isPleading');
    const isPleading = isPleadingParam === 'true' ? true : isPleadingParam === 'false' ? false : undefined;
    return {
      ...(types.length > 0 ? { types } : {}),
      ...(levels.length > 0 ? { levels } : {}),
      ...(sources.length > 0 ? { sources } : {}),
      ...(hasRelevantFacts ? { hasRelevantFacts: true } : {}),
      ...(isPleading !== undefined ? { isPleading } : {}),
    };
  });

  // Document filter UI state (Maps for DocumentFilter component, derive string[] for API)
  const [includedDocs, setIncludedDocs] = useState<Map<string, MyOptionType>>(new Map());
  const [excludedDocs, setExcludedDocs] = useState<Map<string, MyOptionType>>(new Map());

  // Sync state to URL search params
  useEffect(() => {
    const params = new URLSearchParams();
    if (pagination.pageSize !== 50) params.set('pageSize', String(pagination.pageSize));
    if (pagination.pageIndex > 0) params.set('page', String(pagination.pageIndex));
    if (searchQuery) params.set('search', searchQuery);

    if (filters.types && filters.types.length > 0) {
      filters.types.forEach((t) => params.append('types', t));
    }
    if (filters.levels && filters.levels.length > 0) {
      filters.levels.forEach((l) => params.append('levels', l));
    }
    if (filters.sources && filters.sources.length > 0) {
      filters.sources.forEach((s) => params.append('sources', s));
    }
    if (filters.hasRelevantFacts) {
      params.set('hasRelevantFacts', 'true');
    }
    if (filters.isPleading !== undefined) {
      params.set('isPleading', String(filters.isPleading));
    }

    setSearchParams(params, { replace: true });
  }, [pagination.pageSize, pagination.pageIndex, searchQuery, filters, setSearchParams]);

  // Derive full filters including doc IDs
  const fullFilters = useMemo<InconsistencyFilters>(
    () => ({
      ...filters,
      ...(includedDocs.size > 0 ? { includedDocIds: Array.from(includedDocs.keys()) } : {}),
      ...(excludedDocs.size > 0 ? { excludedDocIds: Array.from(excludedDocs.keys()) } : {}),
    }),
    [filters, includedDocs, excludedDocs],
  );

  const { data: responseData, isLoading: isLoadingInconsistencies } = useGetSubsetInconsistencies({
    matterId,
    subsetId,
    searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters: fullFilters,
  });

  const inconsistencies = responseData?.inconsistencies;
  const totalCount = responseData?.totalCount ?? 0;
  const totalUnfilteredCount = responseData?.totalUnfilteredCount ?? 0;
  const pageCount = responseData?.pageCount ?? 0;

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Inconsistencies', path: 'inconsistencies' },
  });

  const { mutate: deleteInconsistency } = useDeleteInconsistency({ matterId, subsetId });
  const { fetchDownload, isLoadingDownload } = useDownloadInconsistencies({ matterId });
  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);

  const resetPage = () => setPagination((prev) => ({ ...prev, pageIndex: 0 }));

  const updateFilters = (update: Partial<InconsistencyFilters>) => {
    setFilters((prev) => ({ ...prev, ...update }));
    resetPage();
  };

  const handleDocsChange = ({
    includedDocs: newIncluded,
    excludedDocs: newExcluded,
  }: {
    includedDocs?: Map<string, MyOptionType>;
    excludedDocs?: Map<string, MyOptionType>;
  }) => {
    if (newIncluded !== undefined) setIncludedDocs(newIncluded);
    if (newExcluded !== undefined) setExcludedDocs(newExcluded);
    resetPage();
  };

  const clearAllFilters = () => {
    setFilters(defaultFilters);
    setSearchQuery('');
    setIncludedDocs(new Map());
    setExcludedDocs(new Map());
    resetPage();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    resetPage();
  };

  // TanStack table for pagination UI only (manual/server-side pagination)
  const table = useReactTable({
    data: inconsistencies ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    manualPagination: true,
    pageCount,
    state: { pagination },
    onPaginationChange: setPagination,
  });

  const handleDownload = useCallback(
    ({ title, columns, downloadFormat }: { title: string; columns: string[]; downloadFormat: DownloadFormat }) => {
      fetchDownload(
        { subsetId, title, columns, format: downloadFormat, searchQuery, filters: fullFilters },
        {
          onSuccess: async () => {
            await new Promise((resolve) => setTimeout(resolve, 200));
            setDownloadModalIsOpen(false);
          },
        },
      );
    },
    [fetchDownload, subsetId, searchQuery, fullFilters],
  );

  const filterPills = useMemo(
    () =>
      buildInconsistencyFilterPills(fullFilters, (key, value) => {
        if (key === 'includedDocIds') {
          setIncludedDocs(new Map());
          resetPage();
        } else if (key === 'excludedDocIds') {
          setExcludedDocs(new Map());
          resetPage();
        } else if (value !== undefined) {
          setFilters((prev) => {
            const currentValues = (prev[key as keyof InconsistencyFilters] ?? []) as string[];
            const newValues = currentValues.filter((v) => v !== value);
            return { ...prev, [key]: newValues.length > 0 ? newValues : undefined };
          });
          resetPage();
        } else {
          updateFilters({ [key]: undefined });
        }
      }),
    [fullFilters],
  );

  const filterCount = useMemo(() => {
    let count = 0;
    if (filters.hasRelevantFacts) count++;
    if (filters.isPleading !== undefined) count++;
    if (filters.types && filters.types.length > 0) count++;
    if (filters.levels && filters.levels.length > 0) count++;
    if (filters.sources && filters.sources.length > 0) count++;
    if (includedDocs.size > 0) count++;
    if (excludedDocs.size > 0) count++;
    return count;
  }, [filters, includedDocs, excludedDocs]);

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <PageHeader className="flex flex-col items-start gap-2">
            <div className="items flex gap-4">
              <PageTitle text="Inconsistencies" />
              <SubsetChip subsetId={subsetId} className="mt-0.5" />
            </div>
          </PageHeader>

          <div className="flex w-full gap-2">
            <SearchInput
              placeholder="Search inconsistencies..."
              className="grow"
              value={searchQuery}
              onChange={handleSearchChange}
            />

            <PermissionGate permission={Permission.InconsistencyDownload} uiMode="disabled">
              <Dialog open={downloadModalIsOpen} onOpenChange={setDownloadModalIsOpen}>
                <DialogTrigger asChild>
                  <Button disabled={totalCount === 0}>
                    <Icon className="lucide--download" />
                    <span>Download</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Download Inconsistencies</DialogTitle>
                  </DialogHeader>
                  <DownloadTableModal
                    columnOptions={inconsistencyDownloadColumnOptions.map((o) => o.value)}
                    downloadTitle={`${matter?.name ?? 'Inconsistencies'} - ${subset?.name ?? 'Subset'}`}
                    loadingDownload={isLoadingDownload}
                    totalCount={totalCount}
                    handleDownload={handleDownload}
                  />
                </DialogContent>
              </Dialog>
            </PermissionGate>

            <Popover>
              <PopoverTrigger asChild>
                <Button>
                  <Icon className="lucide--funnel-plus" />
                  <span>Add Filters</span>
                  <CountBadge count={filterCount} />
                </Button>
              </PopoverTrigger>
              <FilterPopover
                filters={filters}
                onFiltersChange={updateFilters}
                clearAllFilters={clearAllFilters}
                includedDocs={includedDocs}
                excludedDocs={excludedDocs}
                onDocsChange={handleDocsChange}
              />
            </Popover>

            <Button onClick={clearAllFilters}>
              <Icon className="lucide--funnel-x" />
              <span>Clear All Filters</span>
            </Button>
          </div>

          <FilterPills pills={filterPills} />

          <div className="flex items-center justify-end pt-4 pb-3">
            <TablePagination table={table} plural="inconsistencies" variant="sm" totalCount={totalCount} />
          </div>
        </PageContainer>

        {isLoadingInconsistencies ? (
          <InconsistenciesLoader />
        ) : inconsistencies && inconsistencies.length > 0 ? (
          <Accordion type="multiple" className="mx-5 flex flex-col gap-5 overflow-auto">
            {table.getRowModel().rows.map((row) => (
              <InconsistencyCard
                key={row.id}
                inconsistency={row.original}
                onDelete={() => deleteInconsistency(row.original.id)}
              />
            ))}
          </Accordion>
        ) : (
          <Empty className="mb-32">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--alert-triangle" />
              </EmptyMedia>
              <EmptyTitle>No inconsistencies found</EmptyTitle>
              <EmptyDescription>
                {totalUnfilteredCount === 0
                  ? `There are no inconsistencies in this ${subset?.isAllDocuments ? 'Matter' : 'Subset'}.`
                  : 'No inconsistencies match these filters.'}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
    </>
  );
}
